import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import BigButton from "components/atoms/buttons/BigButton";
import StepperComponent from "components/blocks/stepper/Stepper";
import {
  RecorderTypePermissionWithoutRole,
  UserGroupCURequest,
  UserGroupUserPermission,
} from "api/interfaces/userInterface.interface";
import DeleteConfirmModal from "components/templates/modal/small/confirm/delete/DeleteConfirmModal";
import RecorderPermissionStep from "./steps/RecorderPermission";
import DwPermissionStep from "./steps/DwPermission";
import { AddUserGroupProps, UPDATE_TYPE } from "./AddUserGroupContainer";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { useMemo, useState } from "react";
import { RecorderType } from "./PermissionPolicy";
import * as s from "styles/ModalCommonStyled";
import {
  AccountsStepPage,
  PreferencesStepPage,
  RecordersStepPage,
  UsersStepPage,
} from "./pages";

interface Props extends AddUserGroupProps {
  page: number;
  onChangeGroupInfo: (type: UPDATE_TYPE, value: any) => void;
  userGroupValidate: Validate;
  userGroupValidateMsg: ValidateMsg;
  userGroup: UserGroupCURequest;
  isEditableUser: boolean;
  isNoRecorder: boolean;
  onFocusPreferencePage: () => void;
  onChangeDwPermission: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeRecorderLevel: (value: number, type: RecorderType) => void;
  onClickDelete: () => void;
  onClickCreate: () => void;
  recorderRolepermissionDisabled: boolean;
  onChangeRecorderPermission: (
    type: any,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onChangePage: (name: string, index?: number) => void;
  myDwDisabledList: UserGroupUserPermission;
  recorderPermissionDisabledList: RecorderTypePermissionWithoutRole;
  selectedRecorderTypes: string[];
  deleteDisabled: boolean;
  saveDisabled: boolean;
  madeUserGroupUserId: string;
}

function AddUserGroupPresenter(props: Props): JSX.Element {
  const { isAdminGroup, accountLevel, accountId }: ProfileAccountInfo =
    useAppSelector((state) => state.accountSettings);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);

  const isDisabledAutoApply = () => {
    if (!isAdminGroup) {
      return true;
    }
    return props.isEditableUser
      ? props.userGroup.userPermission.enableAllPermissions
      : true;
  };

  const isEU = accountLevel === LEVEL_TYPE.EU;
  const isEdit = !!props.groupId;
  const disabledAutoApply = isDisabledAutoApply();

  const applyPage = [
    { label: "Users" },
    { label: "myDW Permissions" },
    ...(isEU
      ? [{ label: "Recorders" }, { label: "Recorder Permissions" }]
      : [{ label: "Organizations" }]),
    { label: "Preferences" },
  ];

  const preferencesPage = (
    <PreferencesStepPage
      isEditableUser={props.isEditableUser}
      userGroup={props.userGroup}
      groupNameValidate={props.userGroupValidate.isUserGroupName}
      groupNameValidateMsg={props.userGroupValidateMsg.isUserGroupName}
      onFocusPreferencePage={props.onFocusPreferencePage}
      onChangeGroupInfo={props.onChangeGroupInfo}
    />
  );

  const renderPageContent = () => {
    switch (props.page) {
      case 0:
        return (
          <UsersStepPage
            accountId={accountId}
            groupId={props.groupId}
            isEditableUser={props.isEditableUser}
            userGroup={props.userGroup}
            usersValidateMsg={props.userGroupValidateMsg.isUsers}
            onChangeGroupInfo={props.onChangeGroupInfo}
          />
        );

      case 1:
        return (
          <DwPermissionStep
            isEdit={isEdit}
            userCheckedList={props.userGroup.userPermission}
            myDwDisabledList={props.myDwDisabledList}
            onChangeDwPermission={props.onChangeDwPermission}
            validateMsg={props.userGroupValidateMsg.isUserPermission}
            isValidate={props.userGroupValidate.isUserPermission}
          />
        );

      case 2:
        if (isEU) {
          return (
            <RecordersStepPage
              accountId={accountId}
              isAdminGroup={isAdminGroup}
              isNoRecorder={props.isNoRecorder}
              isSelectionDisabled={isEdit && !props.isEditableUser}
              isDisabledAutoApply={disabledAutoApply}
              recorderValidate={props.userGroupValidate.isRecorder}
              recorderValidateMsg={props.userGroupValidateMsg.isRecorder}
              madeUserGroupUserId={props.madeUserGroupUserId}
              userGroup={props.userGroup}
              onChangeGroupInfo={props.onChangeGroupInfo}
            />
          );
        }

        return (
          <AccountsStepPage
            accountId={accountId}
            isEditableUser={props.isEditableUser}
            madeUserGroupUserId={props.madeUserGroupUserId}
            isDisabledAutoApply={disabledAutoApply}
            userGroup={props.userGroup}
            onChangeGroupInfo={props.onChangeGroupInfo}
          />
        );

      case 3:
        if (!isEU) return preferencesPage;
        return (
          <RecorderPermissionStep
            recorderPermission={props.userGroup.recorderPermission}
            permissionDisabled={props.recorderRolepermissionDisabled}
            onChangeRecorderLevel={props.onChangeRecorderLevel}
            disableList={props.recorderPermissionDisabledList}
            onChangeRecorderPermission={props.onChangeRecorderPermission}
            selectedRecorderTypes={props.selectedRecorderTypes}
            isNoRecorder={props.isNoRecorder && isAdminGroup}
          />
        );

      case 4:
        return isEU ? preferencesPage : null;
      default:
        return null;
    }
  };

  return (
    <BigPopupTemplate
      title={props.groupId ? "Edit User Group" : "Add User Group"}
      onModal={props.onModal}
    >
      <StepperComponent
        activeStep={props.page}
        steps={applyPage}
        onClick={props.onChangePage}
        data-testid="users-user-group-add-stepper-component-change-page"
      />

      <s.ModalInnerHalf>{renderPageContent()}</s.ModalInnerHalf>
      <s.ButtonWrapper>
        {props.groupId && props.isEditableUser && (
          <BigButton
            buttonType="warning"
            label="Delete"
            disabled={props.deleteDisabled}
            onClickBtn={() => setIsDeleteConfirm(true)}
            delay
            data-testid="users-user-group-add-button-set-is-delete-confirm"
          />
        )}

        {props.page !== 0 && (
          <BigButton
            buttonType="cancel"
            label="Previous"
            onClickBtn={() => props.onChangePage("previous")}
            data-testid="users-user-group-add-button-change-page"
          />
        )}

        {props.page === applyPage.length - 1 ? (
          (!props.groupId || props.isEditableUser || isAdminGroup) && (
            <BigButton
              label="Save"
              onClickBtn={props.onClickCreate}
              disabled={props.saveDisabled}
              delay
              data-testid="users-user-group-add-button-click-create"
            />
          )
        ) : (
          <BigButton
            buttonType="cancel"
            label="Next"
            onClickBtn={() => props.onChangePage("next")}
            data-testid="users-user-group-add-button-change-page-2"
          />
        )}
      </s.ButtonWrapper>
      {props.groupId && isDeleteConfirm && (
        <DeleteConfirmModal
          label={`group`}
          onDelete={() => setIsDeleteConfirm(false)}
          onConfirmDelete={() => {
            setIsDeleteConfirm(false);
            props.onClickDelete();
          }}
        />
      )}
    </BigPopupTemplate>
  );
}

export default AddUserGroupPresenter;
